<script setup lang="ts">
  import { ref } from 'vue'
  import { Directory, Filesystem } from '@capacitor/filesystem'
  import { Capacitor } from '@capacitor/core'
  import { watchEffect } from 'vue'

  interface Props {
    src: string
    localFallbackSrc?: string
    className?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    localFallbackSrc: '', //TODO
    className: '',
  })

  const computedSrc = ref('')
  const srcHash = ref('')

  watchEffect(() => {
    const originalSrc = props.src
    void hash(originalSrc).then(async (hash) => {
      srcHash.value = hash

      const result = await Filesystem.getUri({
        path: hash + '.png',
        directory: Directory.External,
      })
      try {
        const newSrc = Capacitor.convertFileSrc(result.uri)
        computedSrc.value = newSrc
      } catch (error) {
        console.warn(error)
      }
    })
  })

  const imgElement = ref(null as HTMLImageElement | null)

  async function cacheImage(data: string) {
    await Filesystem.writeFile({
      path: srcHash.value + '.png',
      data: data,
      directory: Directory.External,
    })
    computedSrc.value = data
  }

  function getImageData() {
    if (computedSrc.value !== props.src) {
      return
    }
    if (imgElement.value) {
      const canvas = document.createElement('CANVAS') as HTMLCanvasElement
      const ctx = canvas.getContext('2d')
      canvas.height = imgElement.value.naturalHeight
      canvas.width = imgElement.value.naturalWidth
      ctx?.drawImage(imgElement.value, 0, 0)
      void cacheImage(canvas.toDataURL())
      return
    }
    throw new Error('Image not loaded yet')
  }

  async function hash(message: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(message)
    const hashBuffer = await crypto.subtle.digest('SHA-1', msgUint8)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('') // convert bytes to hex string

    return hashHex
  }
  function error() {
    if (!computedSrc.value) {
      return
    }
    computedSrc.value = props.src
  }
</script>
<template>
  <img
    v-show="computedSrc"
    ref="imgElement"
    :src="computedSrc"
    :class="className"
    crossorigin="anonymous"
    @load="getImageData"
    @error.prevent="error"
  />
</template>
